import type { ActivityDefinition, Extension, ObservationDefinition } from "@medplum/fhirtypes";
import { CATALOG_MODEL_KIND_SYSTEM, catalogNameToFhir, TREATMENT_DEFINITION_KIND } from "@repo/api";
import {
  AVAILABILITIES,
  type Availability,
  DEFAULT_STANDARD_OBSERVATIONS,
  type StandardObservationKey,
  type StandardParameterReference,
} from "@repo/domain";
import { codeSystemUrl, extensionUrl } from "@repo/fhir-systems";
import { CATALOG_VALUE_URL, type CatalogValueDto, decodeCatalogValue, encodeCatalogValue } from "./catalog-value";
import {
  isStandardParameterExtension,
  parameterExtensions,
  readStandardParameterReference,
} from "./standard-parameter-reference";

/**
 * Where an `ActivityDefinition` row keeps the treatment facts the views read
 * (ADR-0021: views read FHIR rows directly and never import codecs).
 *
 * `ActivityDefinition` has a native field for most of the spine — `url`,
 * `version`, `date`, `title`, `description`, `status`. What a treatment CHARTS is contained
 * `ObservationDefinition`s pointed at by `observationResultRequirement`; what
 * it CONFIGURES has no native field and rides on extensions.
 *
 * A row missing any required scalar THROWS rather than reading as `""`, `NaN`
 * or `Inactive`. A substituted value is indistinguishable from a real one, so
 * every broken row would share one lineage url and an unknown status would
 * quietly file a treatment under Inactive. `deriveForSync` in `./base.ts`
 * catches this, logs the resource id and drops that one row.
 *
 * The shared `ActivityDefinition` `deriveFields` hook in `./collections.ts`
 * runs `validateTreatmentDefinitionRow` below on a row tagged as a treatment,
 * so an unreadable one is dropped at sync and never reaches `fn.select`.
 */

export const TREATMENT_PROCEDURE_NOTE_LABEL_URL = extensionUrl("treatment-definition-procedure-note-label");
export const TREATMENT_STANDARD_OBSERVATION_OPT_OUT_URL = extensionUrl(
  "treatment-definition-standard-observation-opt-out",
);
export const TREATMENT_PARAMETER_URL = extensionUrl("treatment-definition-parameter");
export const TREATMENT_DEVICE_REQUIREMENT_URL = extensionUrl("treatment-definition-device-requirement");
export const TREATMENT_LOCATION_REQUIREMENT_URL = extensionUrl("treatment-definition-location-requirement");
export const TREATMENT_COMPONENT_URL = extensionUrl("treatment-definition-component");
export const TREATMENT_OBSERVATION_REPEATABLE_URL = extensionUrl("treatment-observation-repeatable");
export const TREATMENT_OBSERVATION_NOTE_LABEL_URL = extensionUrl("treatment-observation-note-label");
/** The server's own member-id high-water record. Read by the codec, by nothing here. */
export const TREATMENT_MEMBER_IDS_URL = extensionUrl("treatment-definition-member-ids");

/** FHIR requires `ObservationDefinition.code`; the member id fills it without vocabulary meaning. */
const OBSERVATION_CODE_SYSTEM = codeSystemUrl("treatment-observation");

/**
 * The tag that separates a treatment's charted observations from any other
 * contained resource. Re-spelled here because the codec keeps it private; it
 * must track `treatment-definition.codec.ts`.
 */
const OBSERVATION_DEFINITION_KIND = "treatment-observation-definition";

export type TreatmentDefinitionAvailability = Availability;

function unreadable(resource: ActivityDefinition, field: string, value: unknown): never {
  throw new Error(
    `ActivityDefinition row ${resource.id ?? "<unknown>"} has no readable ${field}${
      value === undefined ? "" : ` (${JSON.stringify(value)})`
    }`,
  );
}

export function extension(extensions: Extension[] | undefined, url: string): Extension | undefined {
  return extensions?.find((candidate) => candidate.url === url);
}

export function extensions(list: Extension[] | undefined, url: string): Extension[] {
  return (list ?? []).filter((candidate) => candidate.url === url);
}

/** A member's own child field. The codec writes `valueString`; `valueCode` is read too, as it does. */
export function childText(parent: Extension | undefined, url: string): string | undefined {
  const found = extension(parent?.extension, url);
  return found?.valueString ?? found?.valueCode;
}

/** The lineage canonical, shared by every revision. */
export function treatmentDefinitionUrl(resource: ActivityDefinition): string {
  return resource.url ?? unreadable(resource, "url", undefined);
}

/** Integer, compared numerically: a string sort puts "9" above "10" from revision 10 on. */
export function treatmentDefinitionVersion(resource: ActivityDefinition): number {
  const version = Number(resource.version);
  return resource.version !== undefined && Number.isFinite(version)
    ? version
    : unreadable(resource, "version", resource.version);
}

/** `date` is the MINT date, stamped once — not `meta.lastUpdated`, which a correction moves. */
export function treatmentDefinitionMintedAt(resource: ActivityDefinition): string {
  return resource.date ?? unreadable(resource, "mintedAt", undefined);
}

export function treatmentDefinitionName(resource: ActivityDefinition): string {
  return resource.title ?? unreadable(resource, "name", undefined);
}

export function treatmentDefinitionDescription(resource: ActivityDefinition): string | undefined {
  return resource.description;
}

/**
 * The encoder writes `active` or `retired` and nothing else, so any other
 * status is a row this reader cannot place. Collapsing "not active" into
 * Inactive would file a draft or unknown row as a real retirement.
 */
export function treatmentDefinitionAvailability(resource: ActivityDefinition): TreatmentDefinitionAvailability {
  const availability = resource.status === "active" ? "Active" : resource.status === "retired" ? "Inactive" : undefined;
  return AVAILABILITIES.includes(availability as Availability)
    ? (availability as Availability)
    : unreadable(resource, "availability", resource.status);
}

export function treatmentDefinitionProcedureNoteLabel(resource: ActivityDefinition): string | undefined {
  return extension(resource.extension, TREATMENT_PROCEDURE_NOTE_LABEL_URL)?.valueString;
}

/**
 * Stored as OPT-OUTS, so absence enables the platform defaults: a row written
 * before standards existed still charts them, and an explicit opt-out survives
 * a round trip without persisting any standard member.
 */
export function treatmentDefinitionStandardObservations(
  resource: ActivityDefinition,
): readonly StandardObservationKey[] {
  const optedOut = extensions(resource.extension, TREATMENT_STANDARD_OBSERVATION_OPT_OUT_URL).map(
    (candidate) => candidate.valueCode,
  );
  return DEFAULT_STANDARD_OBSERVATIONS.filter((key) => !optedOut.includes(key));
}

export function treatmentDefinitionLocationRequirement(resource: ActivityDefinition): string | undefined {
  return extension(resource.extension, TREATMENT_LOCATION_REQUIREMENT_URL)?.valueCode;
}

/**
 * Ordered by `observationResultRequirement`, NOT by `contained` order: which
 * observation comes first is authored content, and `contained` is a bag whose
 * order no writer promises to preserve.
 */
export function treatmentDefinitionObservations(resource: ActivityDefinition): ObservationDefinition[] {
  const charted = new Map(
    (resource.contained ?? [])
      .filter(
        (candidate): candidate is ObservationDefinition =>
          candidate.resourceType === "ObservationDefinition" &&
          (candidate.meta?.tag ?? []).some(
            (tag) => tag.system === CATALOG_MODEL_KIND_SYSTEM && tag.code === OBSERVATION_DEFINITION_KIND,
          ),
      )
      .map((candidate) => [candidate.id, candidate] as const),
  );
  return (resource.observationResultRequirement ?? []).flatMap((requirement) => {
    const found = charted.get(requirement.reference?.replace(/^#/, ""));
    return found ? [found] : [];
  });
}

/**
 * Read every field the treatment projection will later need, and discard it.
 *
 * This is the gap the note above describes, now closed. The projection runs
 * inside a live query's `fn.select`, where a throw blanks the whole page
 * instead of costing one row; reading it all here instead means
 * `deriveForSync` in `./base.ts` catches an unreadable row, logs its id and
 * drops just that row before the projection ever meets it.
 *
 * The member reads do not throw on their own — the projection drops a member
 * it cannot express — but walking them is what surfaces a contained panel with
 * no `code` at all, which throws a `TypeError` from inside `fn.select`.
 */
export function validateTreatmentDefinitionRow(resource: ActivityDefinition): void {
  treatmentDefinitionUrl(resource);
  treatmentDefinitionVersion(resource);
  treatmentDefinitionMintedAt(resource);
  treatmentDefinitionName(resource);
  treatmentDefinitionDescription(resource);
  treatmentDefinitionAvailability(resource);
  treatmentDefinitionProcedureNoteLabel(resource);
  treatmentDefinitionLocationRequirement(resource);
  treatmentDefinitionStandardObservations(resource);
  for (const definition of treatmentDefinitionObservations(resource)) {
    definition.code.coding?.find((coding) => coding.display !== undefined);
    extension(definition.extension, TREATMENT_OBSERVATION_REPEATABLE_URL);
    extension(definition.extension, TREATMENT_OBSERVATION_NOTE_LABEL_URL);
    for (const component of extensions(definition.extension, TREATMENT_COMPONENT_URL)) {
      childText(component, "id");
      childText(component, "label");
      decodeCatalogValue(extension(component.extension, CATALOG_VALUE_URL));
    }
  }
  for (const parameter of extensions(resource.extension, TREATMENT_PARAMETER_URL)) {
    if (isStandardParameterExtension(parameter)) {
      readStandardParameterReference(parameter);
      continue;
    }
    childText(parameter, "id");
    childText(parameter, "label");
    decodeCatalogValue(extension(parameter.extension, CATALOG_VALUE_URL));
  }
  for (const requirement of extensions(resource.extension, TREATMENT_DEVICE_REQUIREMENT_URL)) {
    childText(requirement, "id");
    extension(requirement.extension, "definition");
  }
}

// ---------- the optimistic writer ----------

/**
 * The inverse of the readers above, for optimistic rows only.
 *
 * An optimistic row is read back through the SAME projection a synced row
 * takes, so it has to be written in the same storage shape — any difference
 * reads as a flicker when the server row lands. This is the read side's twin of
 * `treatment-definition.codec.ts`'s encode, and the storage test round-trips a
 * capture of that encoder through both directions to prove the two agree.
 *
 * The member-id MARKS are deliberately absent: they are the server's own
 * high-water record, the client has no honest value for them, and nothing on
 * this side reads them. The synced row brings them.
 */

export interface TreatmentDefinitionRowSpine {
  url: string;
  version: number;
  mintedAt: string;
  name: string;
  description?: string;
  availability: Availability;
}

export interface TreatmentDefinitionRowComponent {
  id: string;
  label: string;
  value: CatalogValueDto;
}

export interface TreatmentDefinitionRowObservation {
  id: string;
  label: string;
  repeatable: boolean;
  noteLabel?: string;
  components: readonly TreatmentDefinitionRowComponent[];
}

export interface TreatmentDefinitionRowParameter {
  id: string;
  label: string;
  value: CatalogValueDto;
}

export interface TreatmentDefinitionRowContent {
  procedureNoteLabel?: string;
  standardObservations: readonly StandardObservationKey[];
  observations: readonly TreatmentDefinitionRowObservation[];
  parameters: readonly (TreatmentDefinitionRowParameter | StandardParameterReference)[];
  deviceRequirements: readonly { id: string; definition: string }[];
  locationRequirement?: string;
}

/**
 * A member with no identity is a defect upstream, not a row to paper over with
 * a blank id: the id is what a later revision edits the member by, and a `""`
 * one would collide with every other member that lost its own.
 */
function writtenMemberId(id: string, collection: string): string {
  if (!id) throw new Error(`A treatment definition ${collection} reached persistence with no member identity`);
  return id;
}

function componentToRow(component: TreatmentDefinitionRowComponent): Extension {
  return {
    url: TREATMENT_COMPONENT_URL,
    extension: [
      { url: "id", valueString: writtenMemberId(component.id, "component") },
      { url: "label", valueString: component.label },
      encodeCatalogValue(component.value),
    ],
  };
}

function observationToRow(observation: TreatmentDefinitionRowObservation): ObservationDefinition {
  // The contained id IS the member id, so a stored measurement citing that
  // identity stays readable and a re-save does not renumber anything.
  const id = writtenMemberId(observation.id, "observation");
  return {
    resourceType: "ObservationDefinition",
    id,
    meta: { tag: [{ system: CATALOG_MODEL_KIND_SYSTEM, code: OBSERVATION_DEFINITION_KIND }] },
    code: { coding: [{ system: OBSERVATION_CODE_SYSTEM, code: id, display: observation.label }] },
    preferredReportName: observation.label,
    multipleResultsAllowed: observation.repeatable,
    extension: [
      { url: TREATMENT_OBSERVATION_REPEATABLE_URL, valueBoolean: observation.repeatable },
      ...(observation.noteLabel === undefined
        ? []
        : [{ url: TREATMENT_OBSERVATION_NOTE_LABEL_URL, valueString: observation.noteLabel }]),
      ...observation.components.map(componentToRow),
    ],
  };
}

function parameterToRow(parameter: TreatmentDefinitionRowParameter): Extension {
  return {
    url: TREATMENT_PARAMETER_URL,
    extension: [
      { url: "id", valueString: writtenMemberId(parameter.id, "parameter") },
      { url: "label", valueString: parameter.label },
      encodeCatalogValue(parameter.value),
    ],
  };
}

function deviceRequirementToRow(requirement: { id: string; definition: string }): Extension {
  return {
    url: TREATMENT_DEVICE_REQUIREMENT_URL,
    extension: [
      { url: "id", valueString: writtenMemberId(requirement.id, "device requirement") },
      // Unpinned on purpose: the requirement names a DeviceDefinition LINEAGE.
      { url: "definition", valueCanonical: requirement.definition },
    ],
  };
}

export function writeTreatmentDefinitionRow(
  spine: TreatmentDefinitionRowSpine,
  content: TreatmentDefinitionRowContent,
): ActivityDefinition {
  const observations = content.observations.map(observationToRow);
  return {
    resourceType: "ActivityDefinition",
    meta: { tag: [{ system: CATALOG_MODEL_KIND_SYSTEM, code: TREATMENT_DEFINITION_KIND }] },
    url: spine.url,
    version: String(spine.version),
    // The MINT date, stamped once. A correction moves `meta.lastUpdated`, so
    // resolving as-of against that would make an old revision look minted on
    // the day someone fixed a typo.
    date: spine.mintedAt,
    ...catalogNameToFhir(spine.name),
    ...(spine.description === undefined ? {} : { description: spine.description }),
    status: spine.availability === "Active" ? "active" : "retired",
    kind: "ServiceRequest",
    intent: "order",
    contained: observations,
    observationResultRequirement: observations.map((observation) => ({ reference: `#${observation.id}` })),
    extension: [
      ...(content.procedureNoteLabel === undefined
        ? []
        : [{ url: TREATMENT_PROCEDURE_NOTE_LABEL_URL, valueString: content.procedureNoteLabel }]),
      // Stored as OPT-OUTS, the same way they read back: the enabled set is the
      // platform's defaults minus what this revision turned off.
      ...DEFAULT_STANDARD_OBSERVATIONS.filter((key) => !content.standardObservations.includes(key)).map((key) => ({
        url: TREATMENT_STANDARD_OBSERVATION_OPT_OUT_URL,
        valueCode: key,
      })),
      ...parameterExtensions(TREATMENT_PARAMETER_URL, content.parameters, parameterToRow),
      ...content.deviceRequirements.map(deviceRequirementToRow),
      ...(content.locationRequirement === undefined
        ? []
        : [{ url: TREATMENT_LOCATION_REQUIREMENT_URL, valueCode: content.locationRequirement }]),
    ],
  };
}
